import { useState, useEffect, useRef } from "react";
import { X, Eye, EyeOff, Mail, Lock, User, MapPin, Calendar, Loader2, Globe, ArrowLeft, CheckCircle2, KeyRound, Crown, Shield, ChevronDown, ChevronUp, Phone, MessageCircle, Briefcase, Heart, Camera } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 모바일 웹뷰(카카오톡/네이버/인스타 인앱 브라우저)에서 응답 본문을 두 번 읽으면
// "body stream already read" 오류가 발생하므로, 본문을 단 한 번(text)만 읽고 안전하게 파싱한다.
async function readJson(res) {
  let text = "";
  try {
    text = await res.text();
  } catch (e) {
    return {};
  }
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (e) {
    return { detail: text };
  }
}

export const AuthModal = ({ isOpen, onClose, initialMode = "login" }) => {
  const [mode, setMode] = useState(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  // Forgot password state
  const [forgotStep, setForgotStep] = useState(1); // 1: email, 2: code, 3: new password
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  // Email verification state
  const [verifyStep, setVerifyStep] = useState(false);

  // Consent state
  const [showConsent, setShowConsent] = useState(false);
  const [consents, setConsents] = useState({ terms: false, privacy: false, age: false, payment: false, profile_visible: false, marketing: false });
  const [expandedConsent, setExpandedConsent] = useState(null);
  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyCode, setVerifyCode] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);
  const codeInputRefs = useRef([]);

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
      setError("");
      setSuccess("");
    }
  }, [isOpen, initialMode]);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    email: "", password: "", passwordConfirm: "",
    real_name: "", username: "", gender: "", birth_year: "",
    country: "", city: "", introduction: "",
    phone: "", kakao_id: "", occupation: "", single_status: "", single_years: "",
  });

  // 프로필 사진 (가입 필수)
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const photoInputRef = useRef(null);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError("JPG, PNG, GIF, WEBP 형식의 사진만 업로드할 수 있습니다.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("사진 크기는 5MB 이하만 가능합니다.");
      return;
    }
    setError("");
    setProfilePhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  // Emergent Google Auth redirect
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    // Preserve referral code through OAuth redirect (landing -> callback)
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get("ref");
    if (ref) localStorage.setItem("nc_ref_code", ref);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const resetForms = () => {
    setLoginForm({ email: "", password: "" });
    setRegisterForm({ email: "", password: "", passwordConfirm: "", real_name: "", username: "", gender: "", birth_year: "", country: "", city: "", introduction: "", phone: "", kakao_id: "", occupation: "", single_status: "", single_years: "" });
    setProfilePhoto(null); setPhotoPreview("");
    setError(""); setSuccess(""); setShowPassword(false);
    setForgotStep(1); setForgotEmail(""); setForgotCode("");
    setNewPassword(""); setNewPasswordConfirm("");
    setVerifyStep(false); setVerifyEmail(""); setVerifyCode(["", "", "", "", "", ""]);
    setShowConsent(false); setConsents({ terms: false, privacy: false, age: false, marketing: false }); setExpandedConsent(null);
  };

  const handleCancel = () => { resetForms(); onClose(); };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError(""); setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail || "로그인에 실패했습니다.");
      login(data.access_token, data.user);
      onClose();
    } catch (err) { setError(err.message); }
    finally { setIsLoading(false); }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (registerForm.password !== registerForm.passwordConfirm) { setError("비밀번호가 일치하지 않습니다."); return; }
    if (registerForm.password.length < 6) { setError("비밀번호는 6자 이상이어야 합니다."); return; }
    if (!registerForm.real_name?.trim()) { setError("이름을 입력해주세요."); return; }
    if (!registerForm.gender) { setError("성별을 선택해주세요."); return; }
    if (!registerForm.birth_year) { setError("출생년도를 입력해주세요."); return; }
    if (!registerForm.occupation?.trim()) { setError("직업을 입력해주세요."); return; }
    if (!registerForm.single_status) { setError("싱글 여부를 선택해주세요."); return; }
    if (["divorced", "widowed"].includes(registerForm.single_status) && !registerForm.single_years.trim()) { setError("이혼/사별 경과 기간을 입력해주세요. (예: 3년차)"); return; }
    if (!profilePhoto) { setError("프로필 사진을 등록해주세요. (필수)"); return; }
    // 전화번호 / 카카오톡 ID 는 일반(무료)회원 가입 시 선택 항목 (정회원 결제 시 필수)

    // Show consent screen instead of submitting directly
    setShowConsent(true);
  };

  const handleConsentSubmit = async () => {
    if (!consents.terms || !consents.privacy || !consents.age || !consents.payment) {
      setError("필수 항목에 모두 동의해주세요.");
      return;
    }
    setError("");
    setIsLoading(true);
    try {
      // Check for referral code from URL
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get("ref") || localStorage.getItem("nc_ref_code") || "";
      const registerPayload = {
        ...registerForm,
        birth_year: parseInt(registerForm.birth_year),
        location: [registerForm.country, registerForm.city].filter(Boolean).join(", "),
        marketing_consent: consents.marketing,
        payment_terms_consent: consents.payment,
        // 프로필 공개 동의: false면 회원정보보기 목록에서 자동 숨김 처리
        hidden_from_listing: !consents.profile_visible,
        profile_visible_consent: consents.profile_visible,
      };
      if (refCode) registerPayload.referral_code = refCode;
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(registerPayload),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail || "회원가입에 실패했습니다.");
      login(data.access_token, data.user);
      // 가입 직후 필수 프로필 사진 업로드 (방금 발급된 토큰 사용)
      if (profilePhoto && data.access_token) {
        try {
          const fd = new FormData();
          fd.append("file", profilePhoto);
          await fetch(`${API_URL}/api/auth/upload-profile-image`, {
            method: "POST",
            headers: { Authorization: `Bearer ${data.access_token}` },
            body: fd,
          });
        } catch (uploadErr) {
          console.warn("[register] profile photo upload failed", uploadErr);
        }
      }
      setVerifyEmail(registerForm.email);
      setVerifyStep(true);
      setShowConsent(false);
      setResendTimer(60);
    } catch (err) { setError(err.message); }
    finally { setIsLoading(false); }
  };

  const allRequiredConsents = consents.terms && consents.privacy && consents.age && consents.payment;
  const allConsents = consents.terms && consents.privacy && consents.age && consents.payment && consents.marketing;

  const toggleAllConsents = (checked) => {
    setConsents({ terms: checked, privacy: checked, age: checked, marketing: checked });
  };

  // Verification code input handling
  const handleCodeChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...verifyCode];
    next[idx] = val.slice(-1);
    setVerifyCode(next);
    if (val && idx < 5) codeInputRefs.current[idx + 1]?.focus();
  };

  const handleCodeKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !verifyCode[idx] && idx > 0) {
      codeInputRefs.current[idx - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = [...verifyCode];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || "";
    setVerifyCode(next);
    const focusIdx = Math.min(pasted.length, 5);
    codeInputRefs.current[focusIdx]?.focus();
  };

  const handleVerifyEmail = async () => {
    const code = verifyCode.join("");
    if (code.length !== 6) { setError("6자리 인증번호를 입력해주세요."); return; }
    setError(""); setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifyEmail, code }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail || "인증에 실패했습니다.");
      setSuccess("이메일 인증이 완료되었습니다!");
      setTimeout(() => { resetForms(); onClose(); navigate("/welcome-intro"); }, 1500);
    } catch (err) { setError(err.message); }
    finally { setIsLoading(false); }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    setError(""); setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/send-verification`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifyEmail }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail);
      setSuccess("인증번호가 재발송되었습니다.");
      setResendTimer(60);
      setVerifyCode(["", "", "", "", "", ""]);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.message); }
    finally { setIsLoading(false); }
  };

  // Forgot password handlers
  const handleForgotSendCode = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { setError("이메일을 입력해주세요."); return; }
    setError(""); setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail);
      setSuccess(data.message);
      setForgotStep(2);
      setResendTimer(60);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.message); }
    finally { setIsLoading(false); }
  };

  const handleForgotVerifyCode = async (e) => {
    e.preventDefault();
    if (!forgotCode || forgotCode.length !== 6) { setError("6자리 인증번호를 입력해주세요."); return; }
    setError("");
    setForgotStep(3);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== newPasswordConfirm) { setError("비밀번호가 일치하지 않습니다."); return; }
    if (newPassword.length < 6) { setError("비밀번호는 6자 이상이어야 합니다."); return; }
    setError(""); setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail, code: forgotCode, new_password: newPassword }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail);
      setSuccess(data.message);
      setTimeout(() => { setMode("login"); resetForms(); }, 2000);
    } catch (err) { setError(err.message); }
    finally { setIsLoading(false); }
  };

  if (!isOpen) return null;

  // Consent screen after clicking register
  if (showConsent) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleCancel}>
        <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="consent-modal">
          <button onClick={handleCancel} className="absolute top-4 right-4 p-2 text-stone-400 hover:text-stone-600 transition-colors z-10" data-testid="consent-modal-close">
            <X className="w-5 h-5" />
          </button>

          <div className="p-6 pb-3">
            <button onClick={() => { setShowConsent(false); setError(""); }} className="flex items-center gap-1 text-sm text-stone-400 hover:text-stone-600 mb-4" data-testid="consent-back-btn">
              <ArrowLeft className="w-4 h-4" /> 이전으로
            </button>
            <div className="text-center">
              <div className="w-14 h-14 bg-burgundy-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-burgundy-500" />
              </div>
              <h2 className="text-xl font-bold text-stone-800 mb-1">개인정보 수집 및 이용 동의</h2>
              <p className="text-stone-400 text-sm">서비스 이용을 위해 아래 약관에 동의해주세요.</p>
            </div>
          </div>

          {error && <div className="mx-6 mb-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}

          <div className="px-6 pb-6 space-y-3">
            {/* 전체 동의 */}
            <label className="flex items-center gap-3 p-4 bg-stone-50 border-2 border-stone-200 rounded-xl cursor-pointer hover:border-burgundy-300 transition-colors" data-testid="consent-all">
              <input
                type="checkbox"
                checked={allConsents}
                onChange={(e) => toggleAllConsents(e.target.checked)}
                className="w-5 h-5 rounded border-stone-300 text-burgundy-500 focus:ring-burgundy-500 accent-[#722F37]"
              />
              <span className="text-sm font-bold text-stone-800">전체 동의하기</span>
            </label>

            <div className="border border-stone-200 rounded-xl divide-y divide-stone-100 overflow-hidden">
              {/* 이용약관 동의 (필수) */}
              <div>
                <div className="flex items-center gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={consents.terms}
                    onChange={(e) => setConsents({ ...consents, terms: e.target.checked })}
                    className="w-4 h-4 rounded border-stone-300 text-burgundy-500 focus:ring-burgundy-500 accent-[#722F37]"
                    data-testid="consent-terms-checkbox"
                  />
                  <span className="text-sm text-stone-700 flex-1">
                    <span className="text-burgundy-500 font-semibold">[필수]</span> 이용약관 동의
                  </span>
                  <button type="button" onClick={() => setExpandedConsent(expandedConsent === "terms" ? null : "terms")} className="text-stone-400 hover:text-stone-600" data-testid="consent-terms-toggle">
                    {expandedConsent === "terms" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
                {expandedConsent === "terms" && (
                  <div className="px-4 pb-3 text-xs text-stone-500 leading-relaxed bg-stone-50" data-testid="consent-terms-content">
                    <div className="max-h-40 overflow-y-auto p-3 bg-white rounded-lg border border-stone-100">
                      <p className="font-semibold mb-2">제1조 (목적)</p>
                      <p className="mb-2">이 약관은 New Coupling(이하 "회사")이 제공하는 매칭 서비스(이하 "서비스")의 이용조건 및 절차, 회사와 회원 간의 권리·의무 및 책임사항을 규정함을 목적으로 합니다.</p>
                      <p className="font-semibold mb-2">제2조 (용어의 정의)</p>
                      <p className="mb-2">"회원"이라 함은 본 약관에 동의하고 회원가입을 한 자를 말합니다. "서비스"라 함은 회사가 제공하는 온라인 매칭, 커뮤니티, 상담 및 부가 서비스를 말합니다.</p>
                      <p className="font-semibold mb-2">제3조 (약관의 효력)</p>
                      <p className="mb-2">본 약관은 서비스를 이용하고자 하는 모든 회원에 대해 그 효력이 발생합니다. 회사는 필요한 경우 관련 법령에 위배되지 않는 범위 내에서 약관을 개정할 수 있습니다.</p>
                      <p className="font-semibold mb-2">제4조 (서비스의 제공)</p>
                      <p className="mb-2">회사는 프로필 기반 매칭, 온라인 커뮤니티, 1:1 및 그룹 상담, 연애사관학교 교육 콘텐츠 등의 서비스를 제공합니다.</p>
                      <p className="font-semibold mb-2">제5조 (회원의 의무)</p>
                      <p>회원은 타인의 개인정보를 침해하거나 허위 정보를 등록하여서는 안 됩니다. 서비스를 이용하여 얻은 정보를 회사의 사전 동의 없이 상업적 목적으로 이용할 수 없습니다.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* 개인정보 수집 및 이용 동의 (필수) */}
              <div>
                <div className="flex items-center gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={consents.privacy}
                    onChange={(e) => setConsents({ ...consents, privacy: e.target.checked })}
                    className="w-4 h-4 rounded border-stone-300 text-burgundy-500 focus:ring-burgundy-500 accent-[#722F37]"
                    data-testid="consent-privacy-checkbox"
                  />
                  <span className="text-sm text-stone-700 flex-1">
                    <span className="text-burgundy-500 font-semibold">[필수]</span> 개인정보 수집 및 이용 동의
                  </span>
                  <button type="button" onClick={() => setExpandedConsent(expandedConsent === "privacy" ? null : "privacy")} className="text-stone-400 hover:text-stone-600" data-testid="consent-privacy-toggle">
                    {expandedConsent === "privacy" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
                {expandedConsent === "privacy" && (
                  <div className="px-4 pb-3 text-xs text-stone-500 leading-relaxed bg-stone-50" data-testid="consent-privacy-content">
                    <div className="max-h-40 overflow-y-auto p-3 bg-white rounded-lg border border-stone-100">
                      <p className="font-semibold mb-2">1. 수집하는 개인정보 항목</p>
                      <p className="mb-2">필수: 이메일, 비밀번호, 이름, 닉네임, 성별, 출생년도, 거주지역</p>
                      <p className="mb-2">선택: 자기소개, 프로필 사진, 직업, 학력, <strong>결혼이력(미혼·기혼·이혼·사별·재혼)</strong>, 자녀 유무, 종교, 키, 체형, 흡연·음주 여부 (정회원 이상)</p>
                      <p className="font-semibold mb-2">2. 개인정보 수집 및 이용 목적</p>
                      <p className="mb-2">회원 관리: 본인 확인, 개인 식별, 가입 의사 확인, 불량 회원 부정 이용 방지</p>
                      <p className="mb-2">서비스 제공: 매칭 서비스, 커뮤니티, 상담 서비스 제공, 맞춤 콘텐츠 추천 (재혼/이혼 회원을 위한 전용 매칭 및 콘텐츠 포함)</p>
                      <p className="font-semibold mb-2">3. 개인정보 보유 및 이용 기간</p>
                      <p className="mb-2">회원 탈퇴 시까지 보유하며, 탈퇴 후 30일 이내에 파기합니다. 단, 관련 법령에 의해 보존이 필요한 경우 해당 기간 동안 보관합니다.</p>
                      <p className="font-semibold mb-2">4. 민감 정보 및 결혼이력 처리</p>
                      <p className="mb-2"><strong>이혼·사별·재혼 관련 결혼이력</strong>은 회원이 자발적으로 입력하는 선택 항목이며, 동의 시에만 수집·이용됩니다. 해당 정보는 매칭 적합도 산정 및 회원 간 상호 이해를 돕기 위해 사용되며, 별도의 동의 없이 제3자에게 제공되지 않습니다.</p>
                      <p className="font-semibold mb-2">5. 개인정보의 제3자 제공</p>
                      <p>회원의 동의 없이 개인정보를 제3자에게 제공하지 않습니다. 다만, 법령에 의한 경우 및 수사 목적으로 법령에 정해진 절차에 따른 경우는 예외로 합니다.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* 만 19세 이상 확인 (필수) */}
              <div>
                <div className="flex items-center gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={consents.age}
                    onChange={(e) => setConsents({ ...consents, age: e.target.checked })}
                    className="w-4 h-4 rounded border-stone-300 text-burgundy-500 focus:ring-burgundy-500 accent-[#722F37]"
                    data-testid="consent-age-checkbox"
                  />
                  <span className="text-sm text-stone-700">
                    <span className="text-burgundy-500 font-semibold">[필수]</span> 만 19세 이상 성인입니다
                  </span>
                </div>
              </div>

              {/* 결제·환불 약관 (필수) */}
              <div>
                <div className="flex items-center gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={consents.payment}
                    onChange={(e) => setConsents({ ...consents, payment: e.target.checked })}
                    className="w-4 h-4 rounded border-stone-300 text-burgundy-500 focus:ring-burgundy-500 accent-[#722F37]"
                    data-testid="consent-payment-checkbox"
                  />
                  <span className="text-sm text-stone-700 flex-1">
                    <span className="text-burgundy-500 font-semibold">[필수]</span> 결제·환불 약관 동의
                  </span>
                  <button type="button" onClick={() => setExpandedConsent(expandedConsent === "payment" ? null : "payment")} className="text-stone-400 hover:text-stone-600" data-testid="consent-payment-toggle">
                    {expandedConsent === "payment" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
                {expandedConsent === "payment" && (
                  <div className="px-4 pb-3 text-xs text-stone-500 leading-relaxed bg-stone-50" data-testid="consent-payment-content">
                    <div className="max-h-40 overflow-y-auto p-3 bg-white rounded-lg border border-stone-100">
                      <p className="font-semibold mb-2">1. 결제 수단 및 방법</p>
                      <p className="mb-2">유료 멤버십(정회원·VIP) 및 이벤트 참가비, 1:1 상담 비용은 Stripe를 통한 신용카드 결제 또는 회사가 별도로 지정한 결제 수단으로 결제할 수 있습니다.</p>
                      <p className="font-semibold mb-2">2. 연간 멤버십 (1년 단위)</p>
                      <p className="mb-2">유료 멤버십은 <strong>월 구독이 아닌 1년 단위 연간 멤버십</strong>으로 구성됩니다. 결제일로부터 1년간 서비스를 이용할 수 있으며, 만료 전 회원이 별도로 <strong>해지(캔슬)하지 않는 한 동일 조건·동일 금액으로 자동 결제·자동 연장</strong>됩니다. 해지는 마이페이지에서 언제든 가능합니다.</p>
                      <p className="font-semibold mb-2">3. 청약철회 및 환불 기준</p>
                      <p className="mb-2">전자상거래법에 따라 결제일로부터 <strong>7일 이내</strong>이며 유료 서비스를 사용하지 않은 경우 전액 환불됩니다. 단, 콘텐츠 열람·매칭 추천 수령·상담 진행 등 서비스가 일부라도 제공된 경우에는 잔여 기간에 대한 일할 계산 후 환불됩니다.</p>
                      <p className="font-semibold mb-2">4. 이벤트·오프라인 모임 환불</p>
                      <p className="mb-2">이벤트 시작 7일 전: 전액 환불 / 3~6일 전: 50% / 2일 이내·노쇼: 환불 불가. 천재지변 등 회사 사정으로 취소되는 경우 전액 환불됩니다.</p>
                      <p className="font-semibold mb-2">5. 환불 절차</p>
                      <p>고객센터(contact@newcoupling.com)로 요청해주시면 영업일 기준 3~7일 이내에 결제 수단으로 환불됩니다.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* 프로필 공개 동의 (선택) */}
              <div>
                <div className="flex items-center gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={consents.profile_visible}
                    onChange={(e) => setConsents({ ...consents, profile_visible: e.target.checked })}
                    className="w-4 h-4 rounded border-stone-300 text-burgundy-500 focus:ring-burgundy-500 accent-[#722F37]"
                    data-testid="consent-profile-visible-checkbox"
                  />
                  <span className="text-sm text-stone-700 flex-1">
                    <span className="text-stone-400 font-semibold">[선택]</span> 내 프로필을 회원정보보기에 공개
                  </span>
                  <button type="button" onClick={() => setExpandedConsent(expandedConsent === "profile_visible" ? null : "profile_visible")} className="text-stone-400 hover:text-stone-600" data-testid="consent-profile-visible-toggle">
                    {expandedConsent === "profile_visible" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
                {expandedConsent === "profile_visible" && (
                  <div className="px-4 pb-3 text-xs text-stone-500 leading-relaxed bg-stone-50" data-testid="consent-profile-visible-content">
                    <div className="p-3 bg-white rounded-lg border border-stone-100 space-y-2">
                      <p>회원님이 등록하신 프로필(닉네임, 프로필 사진, 자기소개, 성별, 출생년도, 거주지역 등)을 New Coupling의 <strong>"회원정보보기" 목록</strong>에 공개하여 다른 회원들이 회원님을 발견할 수 있게 합니다.</p>
                      <p><strong>동의하지 않으셔도 가입과 서비스 이용에는 제한이 없습니다.</strong> 다만 비공개 상태에서는 다른 회원이 회원님을 검색·열람할 수 없습니다.</p>
                      <p>마이페이지의 <strong>"회원정보보기 노출 ON/OFF"</strong> 스위치에서 언제든 변경할 수 있어요.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* 마케팅 정보 수신 동의 (선택) */}
              <div>
                <div className="flex items-center gap-3 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={consents.marketing}
                    onChange={(e) => setConsents({ ...consents, marketing: e.target.checked })}
                    className="w-4 h-4 rounded border-stone-300 text-burgundy-500 focus:ring-burgundy-500 accent-[#722F37]"
                    data-testid="consent-marketing-checkbox"
                  />
                  <span className="text-sm text-stone-700 flex-1">
                    <span className="text-stone-400 font-semibold">[선택]</span> 마케팅 정보 수신 동의
                  </span>
                  <button type="button" onClick={() => setExpandedConsent(expandedConsent === "marketing" ? null : "marketing")} className="text-stone-400 hover:text-stone-600" data-testid="consent-marketing-toggle">
                    {expandedConsent === "marketing" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
                {expandedConsent === "marketing" && (
                  <div className="px-4 pb-3 text-xs text-stone-500 leading-relaxed bg-stone-50" data-testid="consent-marketing-content">
                    <div className="p-3 bg-white rounded-lg border border-stone-100">
                      <p>이벤트, 프로모션, 신규 서비스 안내 등 마케팅 목적의 이메일 및 알림을 수신합니다. 동의하지 않으셔도 서비스 이용에는 제한이 없으며, 언제든 설정에서 변경 가능합니다.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={handleConsentSubmit}
              disabled={!allRequiredConsents || isLoading}
              className="w-full btn-primary py-3 mt-2"
              data-testid="consent-submit-btn"
            >
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "동의하고 가입하기"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Email verification screen after registration
  if (verifyStep) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleCancel}>
        <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="email-verify-modal">
          <button onClick={handleCancel} className="absolute top-4 right-4 p-2 text-stone-400 hover:text-stone-600 transition-colors" data-testid="verify-modal-close">
            <X className="w-5 h-5" />
          </button>
          <div className="p-8 text-center">
            <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Mail className="w-7 h-7 text-stone-500" />
            </div>
            <h2 className="text-xl font-bold text-stone-800 mb-2">이메일 인증</h2>
            <p className="text-stone-400 text-sm mb-1">
              <span className="font-medium text-stone-600">{verifyEmail}</span>
            </p>
            <p className="text-stone-400 text-sm mb-6">으로 발송된 6자리 인증번호를 입력해주세요.</p>

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
            {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm flex items-center gap-2 justify-center"><CheckCircle2 className="w-4 h-4" />{success}</div>}

            <div className="flex justify-center gap-2 mb-6" onPaste={handleCodePaste}>
              {verifyCode.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => (codeInputRefs.current[idx] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(idx, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                  className="w-11 h-13 text-center text-xl font-bold border-2 border-stone-200 rounded-xl bg-stone-50 focus:outline-none focus:border-stone-500 focus:bg-white transition-colors"
                  data-testid={`verify-code-${idx}`}
                />
              ))}
            </div>

            <Button onClick={handleVerifyEmail} disabled={isLoading || verifyCode.join("").length !== 6} className="w-full btn-primary py-3 mb-3" data-testid="verify-submit-btn">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "인증 확인"}
            </Button>

            <button
              onClick={handleResendCode}
              disabled={resendTimer > 0 || isLoading}
              className="text-sm text-stone-400 hover:text-stone-600 transition-colors disabled:opacity-50"
              data-testid="resend-code-btn"
            >
              {resendTimer > 0 ? `재발송 (${resendTimer}초)` : "인증번호 재발송"}
            </button>

            <div className="mt-4">
              <button onClick={handleCancel} className="text-xs text-stone-400 hover:text-stone-500" data-testid="skip-verify-btn">
                나중에 인증하기
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Forgot password screen
  if (mode === "forgot") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleCancel}>
        <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="forgot-password-modal">
          <button onClick={handleCancel} className="absolute top-4 right-4 p-2 text-stone-400 hover:text-stone-600 transition-colors" data-testid="forgot-modal-close">
            <X className="w-5 h-5" />
          </button>

          <div className="p-6 pb-2">
            <button onClick={() => { setMode("login"); setError(""); setSuccess(""); setForgotStep(1); }} className="flex items-center gap-1 text-sm text-stone-400 hover:text-stone-600 mb-4" data-testid="forgot-back-btn">
              <ArrowLeft className="w-4 h-4" /> 로그인으로 돌아가기
            </button>
            <div className="text-center">
              <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound className="w-7 h-7 text-stone-500" />
              </div>
              <h2 className="text-xl font-bold text-stone-800 mb-1">비밀번호 찾기</h2>
              <p className="text-stone-400 text-sm">
                {forgotStep === 1 && "가입한 이메일을 입력해주세요."}
                {forgotStep === 2 && "이메일로 발송된 인증번호를 입력해주세요."}
                {forgotStep === 3 && "새 비밀번호를 설정해주세요."}
              </p>
            </div>
          </div>

          {error && <div className="mx-6 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}
          {success && <div className="mx-6 mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{success}</div>}

          {/* Step 1: Email */}
          {forgotStep === 1 && (
            <form onSubmit={handleForgotSendCode} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">이메일</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-400/20 focus:border-stone-400"
                    placeholder="가입한 이메일을 입력하세요" required data-testid="forgot-email-input" />
                </div>
              </div>
              <Button type="submit" disabled={isLoading} className="w-full btn-primary py-3" data-testid="forgot-send-code-btn">
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "인증번호 발송"}
              </Button>
            </form>
          )}

          {/* Step 2: Code */}
          {forgotStep === 2 && (
            <form onSubmit={handleForgotVerifyCode} className="p-6 space-y-4">
              <div className="bg-burgundy-50/60 border border-burgundy-100 rounded-xl p-3 text-[13px] text-stone-700 leading-relaxed" data-testid="forgot-email-context">
                <p><span className="font-semibold text-burgundy-700">{forgotEmail}</span> 으로</p>
                <p>6자리 인증번호를 발송했습니다.</p>
                <p className="text-stone-500 text-xs mt-1">메일이 안 보이면 <strong>스팸함</strong>도 확인해주세요.</p>
              </div>
              <div>
                <label className="block text-base font-semibold text-stone-700 mb-2">인증번호 6자리</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type="text" inputMode="numeric" maxLength={6} value={forgotCode} onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full pl-10 pr-4 py-4 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-400/30 focus:border-burgundy-400 text-center tracking-[0.4em] text-2xl font-bold"
                    placeholder="000000" required data-testid="forgot-code-input" autoFocus />
                </div>
                <p className="text-xs text-stone-400 mt-1.5 text-center">인증번호는 10분 동안 유효합니다.</p>
              </div>
              <Button type="submit" disabled={isLoading || forgotCode.length !== 6} className="w-full btn-primary py-4 text-base" data-testid="forgot-verify-code-btn">
                다음 단계 →
              </Button>
              <div className="flex items-center justify-between pt-1">
                <button type="button" onClick={() => { setForgotStep(1); setForgotCode(""); setError(""); }} className="text-sm text-stone-500 hover:text-stone-700" data-testid="forgot-change-email-btn">
                  ← 이메일 다시 입력
                </button>
                <button
                  type="button"
                  onClick={handleForgotSendCode}
                  disabled={resendTimer > 0 || isLoading}
                  className="text-sm text-burgundy-600 hover:text-burgundy-700 font-medium disabled:text-stone-400 disabled:cursor-not-allowed"
                  data-testid="forgot-resend-code-btn"
                >
                  {resendTimer > 0 ? `재발송 (${resendTimer}초)` : "인증번호 재발송"}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: New password */}
          {forgotStep === 3 && (
            <form onSubmit={handleResetPassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">새 비밀번호</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type={showPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-400/20 focus:border-stone-400"
                    placeholder="6자 이상" required data-testid="forgot-new-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">새 비밀번호 확인</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type={showPasswordConfirm ? "text" : "password"} value={newPasswordConfirm} onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-400/20 focus:border-stone-400"
                    placeholder="비밀번호 확인" required data-testid="forgot-new-password-confirm" />
                  <button type="button" onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600" data-testid="toggle-forgot-password-confirm">
                    {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={isLoading} className="w-full btn-primary py-3" data-testid="forgot-reset-btn">
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "비밀번호 변경"}
              </Button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={handleCancel}>
      <div className="relative w-full max-w-md mx-4 bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={handleCancel} className="absolute top-4 right-4 p-2 text-stone-400 hover:text-stone-600 transition-colors" data-testid="auth-modal-close">
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-6 pb-2 text-center">
          <h2 className="font-serif text-2xl font-bold text-burgundy-500 flex items-center justify-center gap-2">
            <img src="/logo.png" alt="New Coupling" className="h-8 w-auto" />
            New <span className="text-burgundy-500">Coupling</span>
          </h2>
          <p className="text-stone-500 text-sm mt-1">
            {mode === "login" ? "다시 만나서 반갑습니다" : "새로운 인연을 시작하세요"}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-200 mx-6">
          <button onClick={() => { setMode("login"); setError(""); }} className={`flex-1 py-3 text-sm font-medium transition-colors ${mode === "login" ? "text-burgundy-500 border-b-2 border-burgundy-500" : "text-stone-400 hover:text-stone-600"}`} data-testid="login-tab">
            로그인
          </button>
          <button onClick={() => { setMode("register"); setError(""); }} className={`flex-1 py-3 text-sm font-medium transition-colors ${mode === "register" ? "text-burgundy-500 border-b-2 border-burgundy-500" : "text-stone-400 hover:text-stone-600"}`} data-testid="register-tab">
            회원가입
          </button>
        </div>

        {error && <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}

        {/* Login Form */}
        {mode === "login" && (
          <form onSubmit={handleLoginSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">이메일</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                  placeholder="이메일을 입력하세요" required data-testid="login-email" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">비밀번호</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input type={showPassword ? "text" : "password"} value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full pl-10 pr-12 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                  placeholder="비밀번호를 입력하세요" required data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-burgundy-500 transition-colors" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot password link + Legacy activation */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Link
                to="/legacy-activate"
                onClick={onClose}
                className="text-sm text-burgundy-600 hover:text-burgundy-800 transition-colors font-medium"
                data-testid="legacy-activate-link"
              >
                기존 LizTV 회원 활성화 →
              </Link>
              <button type="button" onClick={() => { setMode("forgot"); setError(""); }}
                className="text-sm text-stone-400 hover:text-burgundy-500 transition-colors" data-testid="forgot-password-link">
                비밀번호를 잊으셨나요?
              </button>
            </div>

            <Button type="submit" disabled={isLoading} className="w-full btn-primary py-3" data-testid="login-submit">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "로그인"}
            </Button>

            {/* Google OAuth divider + button */}
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-stone-400">또는</span></div>
            </div>
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full py-3 rounded-xl border-2 border-stone-300 bg-white text-stone-700 font-medium hover:bg-stone-50 hover:border-stone-400 transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              구글 계정으로 로그인
            </button>

            <button type="button" onClick={handleCancel}
              className="w-full py-3 rounded-xl border-2 border-stone-300 text-stone-700 font-medium hover:bg-stone-100 hover:border-stone-400 transition-colors" data-testid="login-cancel">
              취소
            </button>
          </form>
        )}

        {/* Register Form */}
        {mode === "register" && (
          <form onSubmit={handleRegisterSubmit} className="p-6 space-y-4">
            {/* 구글 간편가입 */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full py-3 rounded-xl border-2 border-stone-300 bg-white text-stone-700 font-medium hover:bg-stone-50 hover:border-stone-400 transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
              data-testid="google-register-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              구글 계정으로 간편가입
            </button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-stone-400">또는 이메일로 가입</span></div>
            </div>

            {/* 일반 회원 기본정보 안내 배너 */}
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid="register-basic-info-banner">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-burgundy-500" />
                <span className="text-sm font-bold text-stone-800">일반(무료) 회원 기본정보 입력</span>
              </div>
              <p className="text-xs text-stone-500 leading-relaxed">
                정회원 이상은 직업, 학력, 결혼이력 등 <span className="font-semibold text-stone-600">상세 프로필 정보</span>가 추가됩니다.
              </p>
            </div>

            {/* 프로필 사진 (필수) */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">프로필 사진 *</label>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handlePhotoSelect}
                className="hidden"
                data-testid="register-photo-input"
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="w-full flex items-center gap-4 p-4 bg-stone-50 rounded-xl border border-stone-200 hover:border-burgundy-300 transition-colors text-left"
                data-testid="register-photo-button"
              >
                <div className="w-16 h-16 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {photoPreview ? (
                    <img src={photoPreview} alt="프로필 미리보기" className="w-full h-full object-cover" data-testid="register-photo-preview" />
                  ) : (
                    <Camera className="w-6 h-6 text-stone-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-700">
                    {profilePhoto ? "사진 변경하기" : "사진 등록하기"}
                  </p>
                  <p className="text-xs text-stone-400 truncate">
                    {profilePhoto ? profilePhoto.name : "JPG·PNG·WEBP, 5MB 이하"}
                  </p>
                </div>
              </button>
              <p className="mt-1.5 text-[12px] text-stone-400">실제 본인 사진을 등록하시면 매칭 신뢰도가 높아집니다. (가입 시 +5,000P)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">이메일 *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input type="email" value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                  placeholder="이메일을 입력하세요" required data-testid="register-email" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">비밀번호 *</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    className="w-full px-4 pr-10 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    placeholder="6자 이상" required data-testid="register-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-burgundy-500 transition-colors">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">비밀번호 확인 *</label>
                <div className="relative">
                  <input type={showPasswordConfirm ? "text" : "password"} value={registerForm.passwordConfirm}
                    onChange={(e) => setRegisterForm({ ...registerForm, passwordConfirm: e.target.value })}
                    className="w-full px-4 pr-10 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    placeholder="비밀번호 확인" required data-testid="register-password-confirm" />
                  <button type="button" onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-burgundy-500 transition-colors" data-testid="toggle-register-password-confirm">
                    {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">이름 *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input type="text" value={registerForm.real_name} onChange={(e) => setRegisterForm({ ...registerForm, real_name: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                  placeholder="이름을 입력하세요" required data-testid="register-real-name" />
              </div>
              <p className="mt-1.5 text-[12px] text-burgundy-700 bg-burgundy-50 border border-burgundy-100 rounded-lg px-3 py-2 leading-relaxed" data-testid="register-real-name-note">
                🔒 안심하세요! 이름은 <strong>본인 확인과 안전한 만남</strong>을 위해서만 사용되며, <strong>커뮤니티에는 닉네임으로만 표시</strong>됩니다.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">닉네임 *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input type="text" value={registerForm.username} onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                  placeholder="닉네임을 입력하세요" required data-testid="register-username" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">성별 *</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setRegisterForm({ ...registerForm, gender: "male" })}
                    className={`flex-1 py-3 rounded-xl border transition-colors ${registerForm.gender === "male" ? "bg-blue-50 border-blue-300 text-blue-600" : "bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300"}`}
                    data-testid="register-gender-male">남성</button>
                  <button type="button" onClick={() => setRegisterForm({ ...registerForm, gender: "female" })}
                    className={`flex-1 py-3 rounded-xl border transition-colors ${registerForm.gender === "female" ? "bg-rose-50 border-rose-300 text-rose-600" : "bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300"}`}
                    data-testid="register-gender-female">여성</button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">출생년도 *</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type="number" value={registerForm.birth_year} onChange={(e) => setRegisterForm({ ...registerForm, birth_year: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    placeholder="예: 1975" min="1940" max="2000" required data-testid="register-birth-year" />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">거주지역 *</label>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type="text" value={registerForm.country || ""} onChange={(e) => setRegisterForm({ ...registerForm, country: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    placeholder="국가 (예: 한국, USA)" required data-testid="register-country" />
                </div>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type="text" value={registerForm.city || ""} onChange={(e) => setRegisterForm({ ...registerForm, city: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    placeholder="도시 (예: 서울, LA)" required data-testid="register-city" />
                </div>
              </div>
            </div>

            {/* 전화번호 + 카카오톡 ID (일반회원은 선택, 정회원 결제 시 필수) */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  전화번호 <span className="text-[10px] text-stone-400 font-normal">(정회원 결제 시 필수)</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type="tel" value={registerForm.phone} onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    placeholder="010-1234-5678" data-testid="register-phone" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  카카오톡 ID <span className="text-[10px] text-stone-400 font-normal">(정회원 결제 시 필수)</span>
                </label>
                <div className="relative">
                  <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type="text" value={registerForm.kakao_id} onChange={(e) => setRegisterForm({ ...registerForm, kakao_id: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    placeholder="카카오톡 아이디" data-testid="register-kakao-id" />
                </div>
              </div>
            </div>

            {/* 직업 + 싱글여부 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">직업 *</label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                  <input type="text" value={registerForm.occupation} onChange={(e) => setRegisterForm({ ...registerForm, occupation: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                    placeholder="예: 회사원, 자영업" required data-testid="register-occupation" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">싱글 여부 *</label>
                <div className="relative">
                  <Heart className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400 z-10 pointer-events-none" />
                  <select value={registerForm.single_status} onChange={(e) => setRegisterForm({ ...registerForm, single_status: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 appearance-none cursor-pointer"
                    required data-testid="register-single-status">
                    <option value="">선택하세요</option>
                    <option value="single">미혼</option>
                    <option value="divorced">이혼</option>
                    <option value="widowed">사별</option>
                  </select>
                </div>
              </div>
            </div>

            {["divorced", "widowed"].includes(registerForm.single_status) && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {registerForm.single_status === "divorced" ? "이혼" : "사별"} 경과 기간 *
                </label>
                <input type="text" value={registerForm.single_years} onChange={(e) => setRegisterForm({ ...registerForm, single_years: e.target.value })}
                  className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                  placeholder="예: 3년차" data-testid="register-single-years" />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">자기소개 (선택)</label>
              <textarea value={registerForm.introduction} onChange={(e) => setRegisterForm({ ...registerForm, introduction: e.target.value })}
                className="w-full px-4 py-3 bg-stone-50 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300 min-h-[80px] resize-none"
                placeholder="간단한 자기소개를 입력하세요" data-testid="register-introduction" />
            </div>

            <Button type="submit" disabled={isLoading} className="w-full btn-primary py-3" data-testid="register-submit">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "가입하기"}
            </Button>

            {/* 정회원 신청 안내 */}
            <div className="relative border border-amber-200 bg-amber-50/50 rounded-xl p-4" data-testid="register-premium-cta">
              <div className="flex items-center gap-2 mb-1.5">
                <Crown className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-bold text-stone-800">더 많은 매칭 혜택을 원하시나요?</span>
              </div>
              <p className="text-xs text-stone-500 mb-3">정회원이 되시면 상세 프로필, 우선 매칭, 1:1 상담 등 프리미엄 서비스를 이용하실 수 있습니다.</p>
              <button
                type="button"
                onClick={() => { handleCancel(); navigate("/membership"); }}
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-200 flex items-center justify-center gap-2"
                data-testid="register-premium-button"
              >
                <Crown className="w-4 h-4" />
                정회원 신청하기
              </button>
            </div>

            <button type="button" onClick={handleCancel}
              className="w-full py-3 rounded-xl border-2 border-stone-300 text-stone-700 font-medium hover:bg-stone-100 hover:border-stone-400 transition-colors" data-testid="register-cancel">
              취소
            </button>
            <p className="text-xs text-stone-400 text-center">
              가입하기를 누르면 개인정보 동의서가 표시됩니다.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
